import { KeyboardArrowDown, Search } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  InputAdornment,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Organization } from "model/organizationModel";
import AvatarRem from "components/AvatarRem";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface OrganizationSelectorProps {
  organizations: Organization[];
  selectedOrganization?: Organization | null;
  onSelect: (organization: Organization) => void;
}

const getInitials = (name?: string | null) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const getOrganizationAvatarSx = (hasIcon: boolean) => ({
  bgcolor: hasIcon ? "transparent" : "grey.100",
  color: "text.primary",
  fontWeight: 700,
  "& .MuiAvatar-img": {
    objectFit: "contain",
  },
});

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  organizations,
  selectedOrganization,
  onSelect,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);
  const translations = useTranslations();
  const filteredOrganizations = organizations.filter((organization) => {
    const query = search.toLowerCase();

    return (
      organization.name?.toLowerCase().includes(query) ||
      organization.subscriptionname?.toLowerCase().includes(query)
    );
  });

  const closeMenu = () => {
    setAnchorEl(null);
    setSearch("");
  };

  return (
    <React.Fragment>
      <ButtonBase
        id="organization-selector"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          alignItems: "stretch",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
          width: "100%",
          minWidth: 0,
          maxWidth: { xs: "100%", md: 440 },
          overflow: "hidden",
          textAlign: "left",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.5, width: "100%" }}>
          <AvatarRem
            icon={selectedOrganization?.icon || undefined}
            sx={{
              ...getOrganizationAvatarSx(Boolean(selectedOrganization?.icon)),
              width: 40,
              height: 40,
            }}
          >
            {getInitials(selectedOrganization?.name)}
          </AvatarRem>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {getTranslation(translations, 'generic.selected_organization')}
            </Typography>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
              {selectedOrganization?.name || ""}
            </Typography>
          </Box>
          <KeyboardArrowDown color="action" />
        </Stack>
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        PaperProps={{ sx: { mt: 1, width: anchorEl?.clientWidth || 320, maxHeight: 420 } }}
      >
        <Box sx={{ p: 1, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}>
          <TextField
            id="organization-selector-search"
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={getTranslation(translations, "pm.organizationchart.ricerca.placeholder")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                backgroundColor: "white",
              },
            }}
          />
        </Box>
        {filteredOrganizations.map((organization) => (
          <MenuItem
            id={`organization-${organization.id}`}
            key={organization.id}
            selected={organization.id === selectedOrganization?.id}
            onClick={() => {
              onSelect(organization);
              closeMenu();
            }}
          >
            <ListItemAvatar>
              <AvatarRem
                icon={organization.icon || undefined}
                sx={getOrganizationAvatarSx(Boolean(organization.icon))}
              >
                {getInitials(organization.name)}
              </AvatarRem>
            </ListItemAvatar>
            <ListItemText
              primary={organization.name}
              secondary={organization.subscriptionname}
              primaryTypographyProps={{ noWrap: true, fontWeight: 600 }}
              secondaryTypographyProps={{ noWrap: true }}
            />
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};

export default OrganizationSelector;
